import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { customerService, discountService } from "@/lib/database";
import {
  Customer,
  Discount,
  DiscountCustomerUsage,
  ShipmentDiscount,
} from "@/lib/supabase";
import {
  ArrowLeft,
  Plus,
  Eye,
  Save,
  ChevronDownIcon,
  UserPlus,
  UserMinus,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function DiscountDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [discount, setDiscount] = useState<Discount | null>(null);
  const [customerUsages, setCustomerUsages] = useState<DiscountCustomerUsage[]>(
    []
  );
  const [shipmentDiscounts, setShipmentDiscounts] = useState<
    ShipmentDiscount[]
  >([]);
  const [loading, setLoading] = useState(false);

  const [editableDescription, setEditableDescription] = useState("");
  const [editableStackable, setEditableStackable] = useState(false);

  const [editableStartDate, setEditableStartDate] = useState<
    Date | undefined
  >();
  const [editableStartTime, setEditableStartTime] = useState("");
  const [editableEndDate, setEditableEndDate] = useState<Date | undefined>();
  const [editableEndTime, setEditableEndTime] = useState("");
  const [openStartDatePicker, setOpenStartDatePicker] = useState(false);
  const [openEndDatePicker, setOpenEndDatePicker] = useState(false);
  const [hasValidityPeriod, setHasValidityPeriod] = useState(false);

  const [editableUsageLimitPerCustomer, setEditableUsageLimitPerCustomer] =
    useState<number | undefined>();
  const [editableTotalUsageLimit, setEditableTotalUsageLimit] = useState<
    number | undefined
  >();
  const [hasUsageLimits, setHasUsageLimits] = useState(false);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [newCustomerUsage, setNewCustomerUsage] = useState<
    Partial<DiscountCustomerUsage> | undefined
  >();
  const [isAddingUsage, setIsAddingUsage] = useState(false);
  const [assignedCustomerIds, setAssignedCustomerIds] = useState<string[]>([]);

  const loadDiscount = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await discountService.getById(id);

      setDiscount(data);

      setCustomerUsages(data.customer_usages || []);
      setShipmentDiscounts(data.shipment_discounts || []);

      setEditableDescription(data.description || "");
      setEditableStackable(!!data.is_stackable);

      setHasUsageLimits(
        !!(data.usage_limit_per_customer || data.total_usage_limit)
      );
      setEditableUsageLimitPerCustomer(
        data.usage_limit_per_customer ?? undefined
      );
      setEditableTotalUsageLimit(data.total_usage_limit ?? undefined);

      setHasValidityPeriod(!!(data.start_date && data.end_date));
      setEditableStartDate(
        data.start_date ? new Date(data.start_date) : undefined
      );
      setEditableStartTime(
        data.start_date
          ? new Date(data.start_date).toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })
          : "00:00:00"
      );

      setEditableEndDate(data.end_date ? new Date(data.end_date) : undefined);
      setEditableEndTime(
        data.end_date
          ? new Date(data.end_date).toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })
          : "00:00:00"
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      // TODO: Only load last 10 and allow searching in the select box
      // Load all customers for dropdown
      const { customers: customerData } = await customerService.getAll(1000);

      setCustomers(customerData || []);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveDiscountInfo = async () => {
    if (!discount) return;

    try {
      setLoading(true);

      // Combine date and time before converting to ISO string
      const startDateTime =
        hasValidityPeriod && editableStartDate
          ? new Date(
              `${
                editableStartDate.toISOString().split("T")[0]
              }T${editableStartTime}`
            )
          : null;

      const endDateTime =
        hasValidityPeriod && editableEndDate
          ? new Date(
              `${
                editableEndDate.toISOString().split("T")[0]
              }T${editableEndTime}`
            )
          : null;

      if (hasValidityPeriod) {
        if (!startDateTime || !endDateTime) {
          throw new Error(
            "Please select both start and end dates for the validity period."
          );
        }
      }

      const updated = await discountService.update(discount?.id, {
        description: editableDescription,
        start_date: startDateTime?.toISOString() || null,
        end_date: endDateTime?.toISOString() || null,
        is_stackable: editableStackable,
        usage_limit_per_customer:
          hasUsageLimits && editableUsageLimitPerCustomer
            ? editableUsageLimitPerCustomer
            : null,
        total_usage_limit:
          hasUsageLimits && editableTotalUsageLimit
            ? editableTotalUsageLimit
            : null,
      });

      setDiscount(updated);

      toast.success("Discount info updated successfully.", {
        position: "top-center",
      });
    } catch (error) {
      console.error(error);

      toast.error(
        error instanceof Error
          ? error.message
          : "An unexpected error occurred.",
        { position: "top-center" }
      );
    } finally {
      setLoading(false);
    }
  };

  const saveUsageLimit = async (
    usage: DiscountCustomerUsage,
    newLimit: number | null
  ) => {
    if (!usage.id) return;
    setLoading(true);

    try {
      await discountService.updateUsage(usage.id, { usage_limit: newLimit });

      toast.success(
        `Successfully updated usage limit for ${usage.customer?.first_name}.`,
        { position: "top-center" }
      );
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error ? error.message : "An unexpected error occured.",
        {
          position: "top-center",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleNewCustomerUsage = async () => {
    if (!newCustomerUsage || !id) return;

    setLoading(true);

    try {
      const usage = await discountService.createUsage(id, newCustomerUsage);

      setCustomerUsages((prev) => [usage, ...prev]);

      toast.success(
        `Successfully assigned ${usage.customer?.first_name} to discount?.`,
        { position: "top-center" }
      );
      setIsAddingUsage(false);
    } catch (error) {
      console.error(error);

      toast.error(
        error instanceof Error ? error.message : "An unexpected error occured.",
        {
          position: "top-center",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const reassignCustomer = async (usage: DiscountCustomerUsage) => {
    if (!usage.id) return;

    setLoading(true);

    try {
      await discountService.updateUsage(usage.id, {
        usage_limit: discount?.usage_limit_per_customer,
      });

      setCustomerUsages((prev) =>
        prev.map((u) =>
          u.id === usage.id
            ? { ...u, usage_limit: discount?.usage_limit_per_customer }
            : u
        )
      );

      toast.success(
        `Successfully reassigned ${usage.customer?.first_name} to discount?.`,
        { position: "top-center" }
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error instanceof Error ? error.message : "An unexpected error occured.",
        {
          position: "top-center",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const unassignCustomer = async (usage: DiscountCustomerUsage) => {
    if (!usage.id) return;

    setLoading(true);

    try {
      await discountService.updateUsage(usage.id, { usage_limit: -1 });

      setCustomerUsages((prev) =>
        prev.map((u) => (u.id === usage.id ? { ...u, usage_limit: -1 } : u))
      );

      toast.success(
        `Successfully unassigned ${usage.customer?.first_name} from discount?.`,
        { position: "top-center" }
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error instanceof Error ? error.message : "An unexpected error occured.",
        {
          position: "top-center",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setAssignedCustomerIds(customerUsages.map((usages) => usages.customer_id!));
  }, [customerUsages]);

  useEffect(() => {
    if (isAddingUsage) {
      setNewCustomerUsage({
        usage_limit: discount?.usage_limit_per_customer,
      });
    } else {
      setNewCustomerUsage(undefined);
    }
  }, [discount, isAddingUsage]);

  useEffect(() => {
    loadDiscount();
    loadData();
  }, [id]);

  return (
    <div className="space-y-6">
      <div className="flex space-x-4 items-center">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{discount?.code}</h1>
          <p className="text-gray-600">
            {discount?.description || "No description"}
          </p>
        </div>
      </div>

      {/* General Info */}
      <Card>
        <CardHeader>
          <CardTitle>Discount Info</CardTitle>
          <CardDescription>General details of the discount</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Description</Label>
            <Input
              value={editableDescription}
              onChange={(e) => setEditableDescription(e.target.value)}
            />
          </div>

          <div className="flex space-x-6">
            <div>
              <Label>Type</Label>
              <p className="text-sm text-muted-foreground">
                {discount?.discount_type}
              </p>
            </div>

            <div>
              <Label>Amount</Label>
              <p className="text-sm text-muted-foreground">
                {discount?.amount}
              </p>
            </div>
          </div>

          <div className="space-x-2 items-center flex">
            <Checkbox
              checked={hasUsageLimits}
              id="usage-limits"
              onCheckedChange={(checked: boolean) => {
                setHasUsageLimits(checked);

                if (!checked) {
                  setEditableUsageLimitPerCustomer(
                    discount?.usage_limit_per_customer
                      ? discount?.usage_limit_per_customer
                      : undefined
                  );
                  setEditableTotalUsageLimit(
                    discount?.total_usage_limit
                      ? discount?.total_usage_limit
                      : undefined
                  );
                }
              }}
            />

            <Label htmlFor="usage-limits">Usage Limits</Label>
          </div>

          {hasUsageLimits && (
            <div className="flex space-x-4">
              <div className="flex flex-col space-y-2">
                <Label>Usage Limit Per Customer</Label>

                <Input
                  type="number"
                  className="w-full"
                  inputMode="decimal"
                  value={editableUsageLimitPerCustomer ?? ""}
                  onChange={(e) =>
                    setEditableUsageLimitPerCustomer(
                      !!e.target.value ? parseFloat(e.target.value) : undefined
                    )
                  }
                />
              </div>

              <div className="flex flex-col space-y-2">
                <Label>Total Usage Cap</Label>

                <Input
                  type="number"
                  className="w-full"
                  inputMode="decimal"
                  value={editableTotalUsageLimit ?? ""}
                  onChange={(e) =>
                    setEditableTotalUsageLimit(
                      !!e.target.value ? parseFloat(e.target.value) : undefined
                    )
                  }
                />
              </div>
            </div>
          )}

          <div className="space-x-2 items-center flex">
            <Checkbox
              checked={hasValidityPeriod}
              id="validity-period"
              onCheckedChange={(checked: boolean) => {
                setHasValidityPeriod(checked);

                if (!checked) {
                  setEditableStartDate(
                    discount?.start_date
                      ? new Date(discount?.start_date)
                      : undefined
                  );
                  setEditableEndDate(
                    discount?.end_date
                      ? new Date(discount?.end_date)
                      : undefined
                  );
                }
              }}
            />
            <Label htmlFor="validity-period">Validity Period</Label>
          </div>

          {hasValidityPeriod && (
            <div className="flex flex-col gap-4 md:flex-row flex-wrap">
              <div className="flex gap-4 justify-between">
                <div className="flex flex-col gap-3">
                  <Label htmlFor="date-picker" className="px-1">
                    Start Date
                  </Label>
                  <Popover
                    open={openStartDatePicker}
                    onOpenChange={setOpenStartDatePicker}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        id="date-picker"
                        className="w-32 justify-between font-normal"
                      >
                        {editableStartDate
                          ? editableStartDate.toLocaleDateString()
                          : "Select date"}
                        <ChevronDownIcon />
                      </Button>
                    </PopoverTrigger>

                    <PopoverContent
                      className="w-auto overflow-hidden p-0"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={editableStartDate}
                        captionLayout="dropdown"
                        onSelect={(date) => {
                          setEditableStartDate(date);
                          setOpenStartDatePicker(false);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex flex-col gap-3">
                  <Label htmlFor="start-time-picker" className="px-1">
                    Start Time
                  </Label>

                  <Input
                    type="time"
                    id="start-time-picker"
                    step="1"
                    value={editableStartTime}
                    onChange={(e) => setEditableStartTime(e.target.value)}
                    className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                  />
                </div>
              </div>

              <div className="flex gap-4 justify-between">
                <div className="flex flex-col gap-3">
                  <Label htmlFor="date-picker" className="px-1">
                    End Date
                  </Label>

                  <Popover
                    open={openEndDatePicker}
                    onOpenChange={setOpenEndDatePicker}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        id="date-picker"
                        className="w-32 justify-between font-normal"
                      >
                        {editableEndDate
                          ? editableEndDate.toLocaleDateString()
                          : "Select date"}
                        <ChevronDownIcon />
                      </Button>
                    </PopoverTrigger>

                    <PopoverContent
                      className="w-auto overflow-hidden p-0"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={editableEndDate}
                        captionLayout="dropdown"
                        onSelect={(date) => {
                          setEditableEndDate(date);
                          setOpenEndDatePicker(false);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex flex-col gap-3">
                  <Label htmlFor="end-time-picker" className="px-1">
                    End Time
                  </Label>

                  <Input
                    type="time"
                    id="end-time-picker"
                    step="1"
                    value={editableEndTime}
                    onChange={(e) => setEditableEndTime(e.target.value)}
                    className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <Switch
              checked={editableStackable}
              onCheckedChange={(checked) => setEditableStackable(checked)}
            />
            <Label>Stackable</Label>
          </div>
          <Button onClick={saveDiscountInfo} size="sm">
            <Save className="h-4 w-4 mr-1" />
            Save
          </Button>
        </CardContent>
      </Card>

      {/* Customer Usage */}
      <Card>
        <CardHeader className="flex flex-row justify-between">
          <div>
            <CardTitle>Customer Usage</CardTitle>
            <CardDescription>Edit per-customer usage limits</CardDescription>
          </div>

          <Button size="sm" onClick={() => setIsAddingUsage(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Assign Customer
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {isAddingUsage && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white space-y-6 md:space-y-0">
              <div className="flex flex-col">
                <Select
                  value={newCustomerUsage?.customer_id || undefined}
                  onValueChange={(value) =>
                    setNewCustomerUsage((prev) => {
                      return { ...prev, customer_id: value };
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers
                      ?.filter((cu) => !assignedCustomerIds.includes(cu.id))
                      .map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.first_name} {customer.last_name} -{" "}
                          {customer.email}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col space-y-6 md:flex-row md:space-x-6 md:items-center">
                <div className="flex flex-col items-center space-y-1">
                  <Label>Usage Limit</Label>

                  <Input
                    type="number"
                    className="w-full text-center md:w-12"
                    inputMode="decimal"
                    value={newCustomerUsage?.usage_limit ?? ""}
                    onChange={(e) =>
                      setNewCustomerUsage((prev) => {
                        return {
                          ...prev,
                          usage_limit:
                            parseInt(e.target.value, 10) ?? undefined,
                        };
                      })
                    }
                  />
                </div>

                <Button size="sm" onClick={() => handleNewCustomerUsage()}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
          )}

          {customerUsages.map((usage) => (
            <div
              key={usage.id}
              className="space-y-6 md:space-y-0 flex flex-col md:flex-row md:items-center md:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white"
            >
              <div className="flex flex-col items-center md:items-start">
                <div className="font-medium">
                  {usage.customer?.first_name} {usage.customer?.last_name}
                </div>

                <div className="text-sm text-gray-600">
                  {usage.customer?.email}
                </div>
              </div>

              <div className="flex space-y-6 md:space-y-0 md:space-x-6 flex-col md:flex-row">
                <div className="flex space-x-6 justify-between">
                  {usage.usage_limit !== -1 ? (
                    <div className="text-center flex flex-col">
                      <Label>Usage Count / Limit</Label>
                      <p className="text-sm text-muted-foreground">
                        {usage.usage_count || 0} /{" "}
                        {usage.usage_limit && usage.usage_limit > 0
                          ? usage.usage_limit
                          : "∞"}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center flex flex-col">
                      <Label>Usage Count</Label>
                      <p className="text-sm text-muted-foreground">
                        {usage.usage_count || 0}
                      </p>
                    </div>
                  )}

                  {usage.usage_limit !== -1 ? (
                    <div className="flex flex-col items-center space-y-1">
                      <Label>Edit Limit</Label>

                      <Input
                        type="number"
                        className="w-full md:w-12 text-center"
                        inputMode="decimal"
                        value={usage.usage_limit ?? ""}
                        onChange={(e) =>
                          setCustomerUsages((prev) =>
                            prev.map((u) =>
                              u.id === usage.id
                                ? {
                                    ...u,
                                    usage_limit:
                                      parseInt(e.target.value, 10) ?? undefined,
                                  }
                                : u
                            )
                          )
                        }
                      />
                    </div>
                  ) : (
                    <Badge
                      variant="outline"
                      className="text-destructive border-destructive"
                    >
                      Unassigned
                    </Badge>
                  )}
                </div>

                <div className="flex md:space-x-6">
                  {usage.usage_limit !== -1 ? (
                    <div className="space-x-2 flex md:items-center items-start w-full">
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() =>
                          saveUsageLimit(usage, usage.usage_limit ?? null)
                        }
                      >
                        <Save className="h-4 w-4 mr-1" />
                        Save
                      </Button>

                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => unassignCustomer(usage)}
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      className="w-full"
                      onClick={() => reassignCustomer(usage)}
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      Reassign
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {!!!customerUsages.length && (
            <div className="flex justify-center">
              <p className="text-md text-muted-foreground">
                No customers assigned yet.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Shipments Using This Discount */}
      <Card>
        <CardHeader>
          <CardTitle>Shipments Using This Discount</CardTitle>
          <CardDescription>
            Shipments where this discount was applied
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {shipmentDiscounts.map((sd) => (
            <div
              key={sd.id}
              className="flex flex-col space-y-6 md:space-y-0 md:flex-row md:items-center md:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white"
            >
              <div className="flex flex-col items-center md:items-start">
                <p className="font-medium">{sd.shipment?.tracking_number}</p>

                {!!sd.shipment?.created_at && (
                  <p className="text-sm text-gray-600">
                    {new Date(sd.shipment?.created_at).toLocaleDateString()}
                  </p>
                )}

                <p className="text-sm">
                  {sd.shipment?.customer?.first_name}{" "}
                  {sd.shipment?.customer?.last_name}
                </p>

                <p className="text-sm text-gray-600">
                  {sd.shipment?.route === "us_to_ht"
                    ? "USA to Haiti"
                    : "Haiti to USA"}
                </p>
              </div>

              <div className="flex flex-col md:flex-row space-y-6 md:space-y-0 items-center space-x-0 md:space-x-4 mt-2 md:mt-0">
                <div className="flex flex-col items-center space-y-1">
                  <Label>Total Cost</Label>

                  <span className="text-sm text-muted-foreground">
                    ${sd.shipment?.total_cost}
                  </span>
                </div>

                <Button
                  size="sm"
                  onClick={() =>
                    navigate(`/admin/shipments/${sd.shipment?.tracking_number}`)
                  }
                >
                  <Eye className="h-4 w-4 mr-1" />
                </Button>
              </div>
            </div>
          ))}

          {!!!shipmentDiscounts.length && (
            <div className="flex justify-center">
              <p className="text-md text-muted-foreground">
                This discount hasn't been used yet.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
